import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Form, Input, Popconfirm, Spin, Table, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import SiteBreadcrumb from '@/components/SiteBreadcrumb/SiteBreadcrumb';
import { globalStore } from '@/GlobalStore';
import { TeamMembersModel } from './TeamMembersModel';
import './TeamMembersStyle.css';

const TeamMembersPage: React.FC = observer(() => {
  const [model] = useState(() => new TeamMembersModel());
  const navigate = useNavigate();

  useEffect(() => {
    if (!globalStore.sessionChecked) {
      return;
    }
    if (!globalStore.isLoggedIn) {
      return;
    }
    if (!globalStore.currentTeamId || !globalStore.currentUser?.teamList?.length) {
      return;
    }
    if (!globalStore.isCurrentTeamAdmin()) {
      message.error('仅团队管理员可访问');
      navigate('/home');
      return;
    }
    void model.load();
  }, [
    model,
    navigate,
    globalStore.sessionChecked,
    globalStore.isLoggedIn,
    globalStore.currentTeamId,
    globalStore.currentUser?.teamList,
    globalStore.currentUser?.id,
  ]);

  const handleInvite = async () => {
    if (!model.inviteForm.phone.trim()) {
      message.error('请输入手机号');
      return;
    }
    if (!model.inviteForm.realName.trim()) {
      message.error('请输入姓名');
      return;
    }
    const ok = await model.invite();
    if (ok) {
      message.success('成员已添加');
    } else {
      message.error('添加失败，请确认您是团队管理员');
    }
  };

  const columns = [
    {
      title: '成员ID',
      dataIndex: 'id',
      key: 'id',
      width: 140,
    },
    {
      title: '姓名',
      dataIndex: 'realName',
      key: 'realName',
      render: (value: string | undefined) => value?.trim() || '—',
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      render: (value: string | undefined) => value?.trim() || '—',
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: unknown, row: { id: string }) => (
        <Popconfirm
          title="确定移除该成员？"
          onConfirm={async () => {
            const ok = await model.remove(row.id);
            if (ok) {
              message.success('已移除');
            } else {
              message.error('移除失败');
            }
          }}
        >
          <Button type="link" danger>
            移除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="team-members-page">
      <SiteBreadcrumb name={model.teamName ? `${model.teamName} · 成员管理` : '成员管理'} />
      <div className="team-members-panel">
        <div className="team-members-panel-title">邀请成员</div>
        <Form layout="inline" className="team-members-invite-form">
          <Form.Item label="手机号" required>
            <Input
              value={model.inviteForm.phone}
              onChange={e => {
                model.inviteForm.phone = e.target.value;
              }}
              placeholder="11 位手机号"
              maxLength={11}
            />
          </Form.Item>
          <Form.Item label="姓名" required>
            <Input
              value={model.inviteForm.realName}
              onChange={e => {
                model.inviteForm.realName = e.target.value;
              }}
              placeholder="真实姓名"
            />
          </Form.Item>
          <Form.Item label="昵称">
            <Input
              value={model.inviteForm.nickname}
              onChange={e => {
                model.inviteForm.nickname = e.target.value;
              }}
              placeholder="选填"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" loading={model.submitting} onClick={() => void handleInvite()}>
              添加
            </Button>
          </Form.Item>
        </Form>
      </div>
      <Spin spinning={model.loading}>
        <Table rowKey="id" columns={columns} dataSource={model.members} pagination={false} />
      </Spin>
    </div>
  );
});

export default TeamMembersPage;
